import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/Store";
import {
  selectCachedAnimationRenderByName,
  setCachedAnimationRender,
} from "../../app/slices/AnimationsSlice";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import PiButton from "../../components/PiButton";
import { AnimationDetailsDTO } from "../../dto/AnimationDetailsDTO";
import {
  ActorState,
  ActorStateAnimation,
  defaultActorStateAnimation,
} from "../../models/ActorState";
import { AnimationService } from "../../services/AnimationService";
import AnimationLibrary from "../previews/animation/AnimationLibrary";

export default function ActorStateAnimationSelector(props: {
  actorState: ActorState;
  onChange: (animation: ActorStateAnimation) => void;
  variant?: "compact" | "preview";
}) {
  const { actorState, onChange, variant = "compact" } = props;
  const dispatch = useDispatch();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const renderUri = useSelector((state: RootState) =>
    actorState.animation.id
      ? selectCachedAnimationRenderByName(state, actorState.animation.id)
      : undefined
  );
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const animationLabel =
    actorState.animation.stateName?.trim() || "No animation selected";
  const hasSelectedAnimation = Boolean(
    actorState.animation.id || actorState.animation.stateName?.trim()
  );

  useEffect(() => {
    if (!variant || variant !== "preview") return;
    if (!currentOrganization?.id || !actorState.animation.id || renderUri) return;

    AnimationService.GetAnimationPreview(
      currentOrganization.id,
      actorState.animation.id
    ).then((render) => {
      if (!render?.downloadUri) return;

      dispatch(
        setCachedAnimationRender({
          animationName: actorState.animation.id,
          downloadUri: render.downloadUri,
        })
      );
    });
  }, [
    actorState.animation.id,
    currentOrganization?.id,
    dispatch,
    renderUri,
    variant,
  ]);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [renderUri]);

  const onSelectAnimation = (animationDetails: AnimationDetailsDTO) => {
    onChange({
      id: animationDetails.name,
      stateName: animationDetails.animationState,
      layer: animationDetails.layer,
    });

    if (!currentOrganization?.id) return;

    AnimationService.GetAnimationPreview(
      currentOrganization.id,
      animationDetails.name
    ).then((render) => {
      if (!render?.downloadUri) return;

      dispatch(
        setCachedAnimationRender({
          animationName: animationDetails.name,
          downloadUri: render.downloadUri,
        })
      );
    });
  };

  const animationLibrary = isLibraryOpen ? (
    <AnimationLibrary
      isOpen={isLibraryOpen}
      onClose={() => setIsLibraryOpen(false)}
      title="Animation Library"
      width="90vw"
      height="90vh"
      onSubmit={onSelectAnimation}
    />
  ) : null;

  if (variant === "preview" && !hasSelectedAnimation) {
    return (
      <div className="actor-state-animation-selector actor-state-animation-selector--empty flex h-full min-h-0 w-full flex-col items-center justify-center gap-[12px]">
        <div
          className="flex items-center gap-[6px] text-[13px] font-medium text-interaction-primary"
          role="status"
          data-testid="actor-state-no-animation-selected"
        >
          <DirectionsWalkOutlinedIcon
            aria-hidden="true"
            fontSize="inherit"
            className="text-[20px]"
          />
          <span>No animation selected</span>
        </div>
        <div className="h-[36px] w-full max-w-[180px]">
          <PiButton
            variant="piBlueOutlined"
            onClick={() => setIsLibraryOpen(true)}
            data-testid="actor-state-animation-button"
          >
            Select Animation
          </PiButton>
        </div>
        {animationLibrary}
      </div>
    );
  }

  const preview =
    variant === "preview" ? (
      <div
        className="relative flex min-h-[180px] w-full items-center justify-center overflow-hidden rounded-[5px] bg-interaction-softBackground"
        data-testid="actor-state-animation-preview"
      >
        {renderUri && !hasImageError ? (
          <>
            {!isImageLoaded && (
              <div className="absolute inset-0 animate-pulse bg-creator-surface" />
            )}
            <img
              className="h-full w-full object-contain p-[10px]"
              src={renderUri}
              alt={actorState.animation.stateName}
              onLoad={() => setIsImageLoaded(true)}
              onError={() => {
                setHasImageError(true);
                setIsImageLoaded(false);
              }}
            />
          </>
        ) : (
          <div className="flex flex-col items-center gap-[8px] text-creator-text-soft">
            <DirectionsWalkOutlinedIcon fontSize="large" color="inherit" />
            <div className="text-[13px] font-medium">No preview selected</div>
          </div>
        )}
      </div>
    ) : null;

  return (
    <div
      className={`actor-state-animation-selector flex w-full flex-col gap-[12px] ${
        variant === "preview" ? "h-full min-h-0" : ""
      }`}
    >
      {variant !== "preview" && (
        <div className="text-[14px] font-bold text-creator-text">Animation</div>
      )}
      {preview}
      <div className="actor-state-animation-selection flex min-h-[48px] w-full items-center gap-[10px] rounded-[5px] bg-creator-surface px-[12px] py-[8px]">
        <div className="flex h-[32px] w-[32px] shrink-0 items-center justify-center text-[24px] text-creator-text-sub">
          <DirectionsWalkOutlinedIcon fontSize="inherit" color="inherit" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-medium text-creator-text">
            {animationLabel}
          </div>
          <div className="text-[12px] font-medium text-creator-text-sub">
            Layer {actorState.animation.layer ?? 0}
          </div>
        </div>
        {hasSelectedAnimation && (
          <button
            type="button"
            aria-label="Clear animation"
            title="Clear animation"
            className="flex h-[24px] w-[24px] shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-[18px] text-interaction-primary transition-colors hover:bg-interaction-primary-washed focus:outline-none focus-visible:ring-2 focus-visible:ring-interaction-primary"
            onClick={() => onChange({ ...defaultActorStateAnimation })}
            data-testid="actor-state-clear-animation"
          >
            <CloseIcon fontSize="inherit" color="inherit" />
          </button>
        )}
      </div>
      <div className="actor-state-animation-action h-[36px]">
        <PiButton
          variant="piBlueOutlined"
          onClick={() => setIsLibraryOpen(true)}
          data-testid="actor-state-animation-button"
        >
          {actorState.animation.stateName ? "Change Animation" : "Select Animation"}
        </PiButton>
      </div>
      {animationLibrary}
    </div>
  );
}
